import useTerms from "../../../hooks/useTerms";
import React from "react";
import { Box, Typography } from "@mui/material";
import { Spa as SpaIcon } from "@mui/icons-material";
import BodyMap, { VIEW_LABEL } from "../../molecules/BodyMap";
import EmptyState from "../../atoms/EmptyState";
import { DetailList, StatusPill } from "../ClinicRecordList/recordTableParts";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import {
  SESSION_STATUS,
  CONSENT_METHOD_LABEL,
  formatDateTimeTr,
  formatDateTr,
  personName,
} from "./practiceSessionFormat";

const SECTION_SX = {
  px: { xs: 2, md: 3 },
  py: 2,
  borderBottom: `1px solid ${COLORS.slate100}`,
};
const HEADING_SX = { fontSize: TYPO.sectionTitle, fontWeight: 600, color: COLORS.slate700, mb: 1 };
const TEXT_SX = { fontSize: TYPO.body, color: COLORS.slate700, whiteSpace: "pre-wrap", wordBreak: "break-word" };

function TextBlock({ title, value }) {
  if (!String(value || "").trim()) return null;
  return (
    <Box sx={SECTION_SX}>
      <Typography sx={HEADING_SX}>{title}</Typography>
      <Typography sx={TEXT_SX}>{value}</Typography>
    </Box>
  );
}

/**
 * Seansın salt-okunur görünümü — hasta sekmesinin sağ alanı ve klinik geneli
 * listenin drawer'ı. Vücut haritası salt-okunur çizilir.
 *
 * @param {object} session
 * @param {React.ReactNode} [consentAction] - onam satırının yanında aksiyon (ör. PDF)
 */
export default function PracticeSessionDetail({ session, consentAction }) {
  const terms = useTerms();
  if (!session) {
    return (
      <EmptyState variant="illustrated-sm" icon={<SpaIcon />} title="Seans bulunamadı" description="Kayıt silinmiş olabilir." />
    );
  }
  const status = SESSION_STATUS[session.status] || SESSION_STATUS.completed;
  const regions = session.regions || [];
  const recs = session.recommendations || [];
  const consent = session.consent && typeof session.consent === "object" ? session.consent : null;

  return (
    <Box>
      <DetailList
        items={[
          { label: "Uygulama", value: session.practiceName },
          { label: "Tarih", value: formatDateTimeTr(session.performedAt) },
          session.durationMin != null ? { label: "Süre", value: `${session.durationMin} dk` } : null,
          { label: "Uygulayıcı", value: personName(session.practitioner || session.createdBy) || null },
          { label: "Durum", value: <StatusPill label={status.label} tone={status.tone} /> },
          {
            label: "Onam",
            value: consent ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <span>
                  {consent.template?.title || "Onam"} · {formatDateTr(consent.signedAt)} ·{" "}
                  {CONSENT_METHOD_LABEL[consent.method] || "Kağıt"}
                </span>
                {consentAction}
              </Box>
            ) : (
              <Typography component="span" sx={{ fontSize: TYPO.body, color: COLORS.slate500 }}>
                {session.status === "pending_consent" ? `${terms.patientGen} telefonunda onay bekliyor` : "Bağlı onam yok"}
              </Typography>
            ),
          },
        ]}
      />

      {(session.cautionsAcknowledged || []).length > 0 ? (
        <Box sx={SECTION_SX}>
          <Typography sx={HEADING_SX}>Görülen uygunluk uyarıları</Typography>
          {session.cautionsAcknowledged.map((c) => (
            <Typography key={c} sx={{ ...TEXT_SX, color: COLORS.warningDarker }}>
              • {c}
            </Typography>
          ))}
        </Box>
      ) : null}

      <Box sx={SECTION_SX}>
        <Typography sx={HEADING_SX}>
          Uygulama bölgeleri
          {regions.length ? (
            <Box component="span" sx={{ color: COLORS.slate400, fontWeight: 500 }}>{` · ${regions.length}`}</Box>
          ) : null}
        </Typography>
        {regions.length ? (
          <>
            <BodyMap value={regions} readOnly figureWidth={130} />
            {regions.some((r) => r.label) ? (
              <Box sx={{ mt: 1 }}>
                {regions.map((r, i) =>
                  r.label ? (
                    <Typography key={i} sx={{ fontSize: TYPO.label, color: COLORS.slate600 }}>
                      {i + 1}. {VIEW_LABEL[r.view]} — {r.label}
                    </Typography>
                  ) : null
                )}
              </Box>
            ) : null}
          </>
        ) : (
          <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate500 }}>Bölge işaretlenmedi.</Typography>
        )}
      </Box>

      <TextBlock title="Kullanılan malzemeler" value={session.materials} />
      <TextBlock title="Gözlem" value={session.observation} />
      <TextBlock title={`${terms.patient} geri bildirimi`} value={session.patientFeedback} />

      {recs.length > 0 ? (
        <Box sx={SECTION_SX}>
          <Typography sx={HEADING_SX}>Takviye / bitkisel öneriler</Typography>
          {recs.map((r, i) => (
            <Box key={`${r.name}-${i}`} sx={{ py: 0.75, borderBottom: i < recs.length - 1 ? `1px solid ${COLORS.slate100}` : "none" }}>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, fontWeight: 500 }}>{r.name}</Typography>
              <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
                {[r.usage, r.duration].filter(Boolean).join(" · ") || "—"}
              </Typography>
              {r.note ? <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate600 }}>{r.note}</Typography> : null}
            </Box>
          ))}
          <Typography sx={{ mt: 1, fontSize: TYPO.caption, color: COLORS.slate400 }}>
            Öneriler reçete yerine geçmez.
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
